<template>
  <v-card id="edit-price-drops">
    <v-card-title class="i-dialog-header-div">
      <v-row class="align-center">
        <v-col cols="12" sm="9">
          <span class="g-bold g-heading-l">{{
            newSpecial ? 'New Price Drop' : 'Edit Price Drop'
          }}</span>
        </v-col>
        <v-col cols="12" sm="3">
          <i-instructions
            instructionKey="data-maintenance/price-drops/add-dialog"
          ></i-instructions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="py-8 i-card-content-div">
      <template v-if="!newSpecial && special.promotion.approval">
        <v-alert
          v-if="special.promotion.approval.status === 'REJECTED'"
          border="left"
          class="i-alert"
          color="warning"
          dense
          elevation="1"
          outlined
          text
          type="error"
        >
          <div class="font-weight-bold mb-3">
            Your Price Drop requires the following changes to be made:
          </div>
          <div>
            <pre class="i-approval-message">{{
              special.promotion.approval.message
            }}</pre>
          </div>
        </v-alert>
      </template>

      <v-alert
        border="left"
        outlined
        dense
        text
        type="info"
        elevation="1"
        class="mt-1 mb-5 i-alert"
        v-if="!newSpecial"
      >
        <template v-if="activeSpecial"
          >For Active Price Drops only the End Date can be edited</template
        >
        <template v-else
          >For Upcoming Price Drops only Date Range, adding or removing
          pre-selected Target Types & Price Drops Pricing in Zones can be
          edited</template
        >
      </v-alert>

      <span class="g-bold g-heading-s g-text-color-black">Price Drop Type</span>
      <legacy-radio-group
        :disabled="!newSpecial"
        name="edit-price-drops-type"
        :default-col-props="{ cols: '12', md: '4', class: 'pb-0' }"
        :options="[
          { label: 'By Family Group', value: BY_FAMILY_GROUP },
          { label: 'By Product (Live products only)', value: BY_PRODUCT }
        ]"
        v-model="specialType"
        @change="resetSpecialForm"
      />

      <template v-if="familySpecial">
        <div class="mt-3 mt-md-5">
          <span class="g-bold g-heading-s g-text-color-black"
            >Family Group</span
          >
        </div>
        <v-text-field
          v-if="activeSpecial"
          background-color="#0000000f"
          class="mt-4"
          dense
          disabled
          :value="lockedFamilyGroupLabel"
          label="Select Family Group *"
          outlined
        ></v-text-field>
        <v-autocomplete
          v-else
          attach
          background-color="#0000000f"
          class="mt-4"
          clearable
          dense
          :disabled="!newSpecial"
          :items="familyGroups"
          item-disabled="disabled"
          label="Select Family Group *"
          outlined
          :rules="[(v) => !!v || 'Family Group selection required']"
          small-chips
          v-model="selectedFamilyGroup"
          @change="handleFamilyGroupSelection"
        >
          <template v-slot:selection>
            <v-tooltip
              bottom
              content-class="family-tooltip"
              transition="fade-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip v-bind="attrs" v-on="on" small class="ma-1" outlined>
                  {{ selectedFamilyGroup.name }}
                </v-chip>
              </template>

              <div class="tooltip-content">
                <strong>Products in this Family Group:</strong>
                <ul>
                  <li
                    v-for="(productName, idx) in getFamilyGroupProductsById(
                      selectedFamilyGroup.id
                    )"
                    :key="idx"
                  >
                    {{ productName }}
                  </li>
                </ul>
              </div>
            </v-tooltip>
          </template>
        </v-autocomplete>
        <v-alert
          v-if="showUnavailableProductAlert"
          border="left"
          dense
          text
          outlined
          type="warning"
          elevation="1"
          class="mt-3 i-alert"
        >
          {{ upcomingPriceChangeWarningText }}
        </v-alert>
      </template>

      <template v-if="productSpecial">
        <div class="mt-3 mt-md-5">
          <span class="g-bold g-heading-s g-text-color-black">Product</span>
        </div>
        <v-text-field
          v-if="activeSpecial"
          background-color="#0000000f"
          class="mt-4"
          dense
          disabled
          :value="lockedProductLabel"
          label="Select Product *"
          outlined
        ></v-text-field>
        <v-autocomplete
          v-else
          attach
          background-color="#0000000f"
          class="mt-4"
          clearable
          dense
          :disabled="!newSpecial"
          :items="visibleProducts"
          item-title="text"
          item-text="text"
          item-value="value"
          item-disabled="disabled"
          label="Select Product *"
          outlined
          :rules="[(v) => !!v || 'Product selection required']"
          v-model:search-input="productSearchInput"
          small-chips
          v-model="selectedProduct"
          @change="handleProductChange"
        >
          <template v-slot:selection>
            {{ selectedProduct.supplierItemId }} /
            {{ selectedProduct.tradeItemDescription }}
          </template>
        </v-autocomplete>
        <v-alert
          v-if="showUnavailableProductAlert"
          border="left"
          dense
          text
          outlined
          type="warning"
          elevation="1"
          class="mt-3 i-alert"
        >
          {{ upcomingPriceChangeWarningText }}
        </v-alert>
      </template>

      <div class="mt-2 mt-md-4">
        <span class="g-bold g-heading-s g-text-color-black">
          Price Drop Date Range
          <v-icon
            class="i-tooltip-color pb-1"
            small
            v-tooltip="
              'Starts at 12:00am & Ends at 11:59pm on the submitted dates'
            "
          >
            mdi-help-circle-outline
          </v-icon>
        </span>
      </div>
      <v-row align="center" class="mt-1">
        <v-col cols="12" sm="6">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                outlined
                readonly
                append-icon="mdi-calendar"
                background-color="#0000000f"
                class="mt-0"
                dense
                label="Start Date *"
                v-on="on"
                v-model="startDateDisplay"
                :disabled="!canEditStartDate"
                :rules="[
                  (v) => !!v || 'Start Date required',
                  validStartSate(startDate, endDate)
                ]"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="minAllowedStartDate"
              v-model="startDate"
              @change="startDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                outlined
                readonly
                label="End Date *"
                append-icon="mdi-calendar"
                background-color="#0000000f"
                dense
                v-on="on"
                v-model="endDateDisplay"
                :disabled="!canEditEndDate"
                :rules="[
                  (v) => !!v || 'End Date required',
                  validEndDate(startDate, endDate)
                ]"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="minAllowedEndDate"
              :picker-date="minAllowedEndDate.slice(0, 7)"
              v-model="endDate"
              @change="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <div class="mt-2">
        <span class="g-bold g-heading-s g-text-color-black">Target Type</span>
      </div>
      <legacy-radio-group
        :disabled="!newSpecial || !(selectedProduct || selectedFamilyGroup)"
        name="edit-price-drops-target-type"
        :default-col-props="{ cols: '12', md: '4', class: 'pb-0' }"
        :options="[
          { label: 'All Zones', value: NATIONAL },
          { label: 'Banner Group(s)', value: BANNER_GROUP },
          {
            label: 'Multi-Store Operator(s)',
            value: MULTI_STORE_OPERATOR
          },
          {
            label: isILR ? 'Customer(s)' : 'Retailer(s)',
            value: RETAILER_STORE
          }
        ]"
        v-model="targetType"
        @change="resetTargetTypeForm"
      />

      <template v-if="targetType === BANNER_GROUP">
        <div class="mt-3 mt-md-5">
          <span class="g-bold g-heading-s g-text-color-black"
            >Banner Group(s)</span
          >
        </div>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-4 mb-6"
          clearable
          deletable-chips
          dense
          :disabled="activeSpecial"
          :items="targetBannerGroups"
          label="Select Banner Group(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || '']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetBannerGroupIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="targetType === MULTI_STORE_OPERATOR">
        <div class="mt-3 mt-md-5">
          <span class="g-bold g-heading-s g-text-color-black"
            >Multi-Store Operator(s)</span
          >
        </div>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-3 mb-6"
          clearable
          deletable-chips
          dense
          :disabled="activeSpecial"
          :items="targetMultiStoreOperators"
          label="Select Multi-Store Operator(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || '']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetMultiStoreOperatorIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="targetType === RETAILER_STORE">
        <div class="mt-3 mt-md-5">
          <span class="g-bold g-heading-s g-text-color-black">{{
            isILR ? 'Customer(s)' : 'Retailer(s)'
          }}</span>
        </div>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-3 mb-9"
          clearable
          deletable-chips
          dense
          :disabled="activeSpecial"
          hide-details
          :items="targetStores"
          :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
          :item-disabled="savedListApplied ? () => true : () => false"
          multiple
          outlined
          :rules="[(v) => !!v.length || ``]"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetStoreIds"
          @change="searchInput = ''"
          @click:clear="resetRetailers"
        ></v-autocomplete>
        <retailer-list-action-button
          class="mb-2"
          :supplier-id="selectedSupplierId"
          :selected-retailers="targetStoreIds"
          :disabled="activePromotion"
          mode="useOnly"
          @apply="handleApplySavedRetailers"
          :saved-list-applied="savedListApplied"
        />
      </template>

      <p
        v-if="isUsTenant"
        class="g-bold g-heading-s g-text-color-black mt-2 mt-md-4"
      >
        Search State (Optional)
      </p>
      <i-search-post-code v-if="!isUsTenant" dense />
      <v-text-field
        v-if="isUsTenant"
        v-model="zoneFilter"
        dense
        outlined
        clearable
        class="mt-3"
        label="Search State"
        hint="Type state code or state name"
        persistent-hint
      />

      <span class="g-bold g-heading-s g-text-color-black"
        >Price Drops by {{ isUsTenant ? 'State' : 'Zone' }}</span
      >
      <legacy-radio-group
        v-if="selectedProduct || selectedFamilyGroup"
        v-model="discountEntryMode"
        name="price-drop-discount-entry-mode"
        :options="discountEntryModeOptions"
        :disabled="activeSpecial"
        :default-col-props="{ cols: '12', sm: '3', class: 'py-0' }"
        class="mt-3 mb-2"
      />

      <v-alert
        border="left"
        outlined
        dense
        text
        type="info"
        elevation="1"
        class="mt-3 i-alert"
      >
        For Zones without promotions, leave blank
      </v-alert>
      <update-price-to-all-zones
        mode="priceDrop"
        :disabled="bulkZoneUpdatesDisabled"
        :hide-unit-rrp="isServiceProductSelected || isUsTenant"
        :is-service-product="isServiceProductSelected"
        v-model:discount-entry-mode="discountEntryMode"
        v-model:enabled="applyToAllZones"
        v-model:carton-price="globalCartonPrice"
        v-model:unit-rrp="globalUnitRrp"
        :zones="regionalPriceMap"
        @update-carton="onGlobalCartonUpdate"
        @update-discount-percentage="onGlobalDiscountPercentageUpdate"
        @update-rrp="onGlobalUnitRrpUpdate"
      />

      <v-alert
        v-if="hasNoPricedZones"
        border="left"
        dense
        outlined
        text
        type="error"
        elevation="1"
        class="my-2 i-alert"
      >
        <span>
          At least 1 {{ isUsTenant ? 'state' : 'zone' }} with valid pricing is
          required
        </span>
      </v-alert>
      <v-alert
        v-else-if="hasIncompletePricing"
        border="left"
        dense
        outlined
        text
        type="error"
        elevation="1"
        class="my-2 i-alert"
      >
        <span>Zone(s) with incomplete pricing</span>
      </v-alert>
      <v-alert
        v-else-if="hasInvalidPricing"
        border="left"
        dense
        outlined
        text
        type="error"
        elevation="1"
        class="my-2 i-alert"
      >
        <span>Zone(s) with invalid pricing</span>
      </v-alert>

      <v-simple-table
        dense
        fixed-header
        light
        class="data-table-zone-pricing elevation-1"
        :height="pricingTableHeight"
      >
        <template v-slot:default>
          <thead>
            <tr>
              <th
                class="text-center py-3 text-uppercase"
                style="width: 10%; min-width: 50px"
              >
                <span>ZONES</span>
              </th>

              <th
                class="text-center py-3 text-uppercase"
                style="width: 13%; min-width: 90px"
              >
                <span>{{
                  isServiceProductSelected
                    ? 'PRICE (EX TAX)'
                    : 'CARTON PRICE (EX TAX)'
                }}</span>
              </th>

              <th
                class="text-center py-3 text-uppercase"
                style="width: 15%; min-width: 110px"
              >
                <span>{{
                  isServiceProductSelected
                    ? 'AMOUNT OFF (EX TAX)'
                    : 'AMOUNT OFF CARTON (EX TAX)'
                }}</span>
              </th>

              <template v-if="variableWeightProductSelected">
                <th
                  class="text-center py-3 text-uppercase"
                  style="width: 12%; min-width: 90px"
                >
                  <span>REVISED COST / KG <br />(EX TAX)</span>
                </th>
                <th
                  v-if="showIncTaxColumns"
                  class="text-center py-3 text-uppercase"
                  style="width: 12%; min-width: 90px"
                >
                  <span>REVISED COST / KG <br />(INC TAX)</span>
                </th>
              </template>
              <template v-else>
                <th
                  class="text-center py-3 text-uppercase"
                  style="width: 12%; min-width: 90px"
                >
                  <span>REVISED UNIT <br />COST (EX TAX)</span>
                </th>
                <th
                  v-if="showIncTaxColumns"
                  class="text-center py-3 text-uppercase"
                  style="width: 12%; min-width: 90px"
                >
                  <span>REVISED UNIT <br />COST (INC TAX)</span>
                </th>
              </template>

              <th
                v-if="showUnitRrpColumns && !isUsTenant"
                class="text-center py-3"
                style="width: 14%; min-width: 120px"
              >
                <span>ORIGINAL UNIT RRP</span><br /><span class="i-color-red"
                  >(INC TAX)</span
                >
              </th>

              <th
                v-if="showUnitRrpColumns && !isUsTenant"
                class="text-center py-3 text-uppercase"
                style="width: 14%; min-width: 110px"
              >
                <template v-if="variableWeightProductSelected">
                  <span>PRICE DROP RRP / KG</span><br /><span
                    class="i-color-red"
                    >(INC TAX)</span
                  >
                </template>
                <template v-else>
                  <span>PRICE DROP UNIT RRP</span><br /><span
                    class="i-color-red"
                    >(INC TAX)</span
                  >
                </template>
              </th>

              <th
                v-if="showUnitRrpColumns && !isUsTenant"
                class="text-center py-3 text-uppercase"
                style="width: 8%; min-width: 80px"
              >
                <span>GP%</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="selectedProduct || selectedFamilyGroup">
            <tr :key="zone.code" v-for="zone in filteredRegionalPriceMap">
              <td class="text-center text-only">{{ zone.displayName }}</td>
              <td class="text-center text-only">
                {{ $filters.currency(zone.cartonPrice) }}
              </td>
              <td>
                <v-form :ref="`formAmountOff${zone.code}`" @submit.prevent>
                  <i-numeric-field
                    dense
                    hide-details="auto"
                    outlined
                    :label="amountOffLabel"
                    :min="0"
                    :max="amountOffMax"
                    :precision="2"
                    :prefix="amountOffPrefix"
                    :suffix="amountOffSuffix"
                    :empty-zero="isPercentageDiscountMode"
                    :disabled="
                      activeSpecial ||
                      isCalculatingProductPriceForRegion(zone.code)
                    "
                    :loading="isCalculatingProductPriceForRegion(zone.code)"
                    :rules="[
                      alertOnEmptyRowFields(
                        zone.amountOff,
                        showUnitRrpColumns ? [zone.unitRrp] : []
                      ),
                      rulesSpecialAmountOff(zone.amountOff, zone.cartonPrice)
                    ]"
                    text-align-right
                    :value="amountOffInputValue(zone)"
                    @input="onAmountOffInput(zone, $event)"
                  >
                  </i-numeric-field>
                  <div
                    v-if="isPercentageDiscountMode && zone.amountOff"
                    class="amount-off-helper"
                  >
                    Amount off: {{ $filters.currency(zone.amountOff) }}
                  </div>
                </v-form>
              </td>
              <td class="i-td-chip">
                <v-chip class="chip">
                  <template v-if="variableWeightProductSelected">
                    {{ $filters.currency(zone.revisedCostPerKGExTax) }}
                  </template>
                  <template v-else>
                    {{ $filters.currency(zone.revisedUnitPriceExTax) }}
                  </template>
                </v-chip>
              </td>

              <td v-if="showIncTaxColumns" class="i-td-chip">
                <v-chip class="chip" color="white">
                  <template v-if="variableWeightProductSelected">
                    {{ $filters.currency(zone.revisedCostPerKGIncTax) }}
                  </template>
                  <template v-else>
                    {{ $filters.currency(zone.revisedUnitPriceIncTax) }}
                  </template>
                </v-chip>
              </td>

              <td
                v-if="showUnitRrpColumns && !isUsTenant"
                class="text-center text-only"
              >
                {{ $filters.currency(getOriginalProductRrp(zone)) }}
              </td>

              <td v-if="showUnitRrpColumns && !isUsTenant">
                <v-form :ref="`formSpecialUnitRrp${zone.code}`" @submit.prevent>
                  <i-numeric-field
                    dense
                    hide-details="auto"
                    outlined
                    :min="0"
                    :max="9999.99"
                    :precision="2"
                    :prefix="currencySymbol"
                    :disabled="
                      activeSpecial ||
                      isCalculatingProductPriceForRegion(zone.code)
                    "
                    :label="
                      variableWeightProductSelected
                        ? 'Price Drop RRP / KG'
                        : 'Price Drop Unit RRP'
                    "
                    :loading="isCalculatingProductPriceForRegion(zone.code)"
                    :rules="[
                      alertOnEmptyRowFields(zone.unitRrp, [zone.amountOff]),
                      rulesSpecialUnitRrp(
                        zone.unitRrp,
                        zone.grossProfitPercentage,
                        variableWeightProductSelected
                      )
                    ]"
                    text-align-right
                    v-model="zone.unitRrp"
                    @input="
                      (calcPricingForZone(zone), onZoneFieldChange(zone.code))
                    "
                  >
                  </i-numeric-field>
                </v-form>
              </td>

              <td
                v-if="showUnitRrpColumns && !isUsTenant"
                class="pb-2 i-td-chip"
              >
                <v-chip
                  class="chip"
                  :class="{
                    negative: zone.grossProfitPercentage < 0,
                    positive: zone.grossProfitPercentage > 0
                  }"
                >
                  {{ zone.grossProfitPercentage || '-' }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="$emit('cancel')"
        >Cancel</v-btn
      >
      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        :disabled="!validPriceDrop"
        :loading="saving"
        @click="savePriceDrop"
        >{{ newSpecial ? 'Create' : 'Update' }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import { calculateProductPrice } from '@/services/products'
import { createPriceDrop, updatePriceDrop } from '@/services/priceDrops'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'
import iNumericField from '@/components/NumericField.vue'
import iSearchPostCode from '@/components/SearchPostCode'
import FieldValidators from '@/utils/FieldValidators'
import SpecialsHelper from '@/modules/dataMaintenance/helper/SpecialsHelper'
import {
  minAllowedPromotionEndDate,
  minAllowedPromotionStartDate
} from '@/modules/dataMaintenance/helper/PromotionPublishSchedule'
import iInstructions from '@/components/Instructions'
import RetailerListActionButton from '@/components/RetailerListActionButton.vue'
import UpdatePriceToAllZones from '@/components/UpdatePriceToAllZones.vue'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { buildVisibleProductOptions } from '@/modules/dataMaintenance/helper/ProductAutocompleteHelper'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange,
  UPCOMING_PRICE_CHANGE_WARNING_TEXT
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'
import {
  amountOffFromDiscountPercentage,
  discountPercentageFromAmountOff,
  DISCOUNT_ENTRY_MODE,
  formatDiscountPercentage,
  setDiscountPercentageFromAmountOff
} from '@/modules/dataMaintenance/helper/DiscountEntryHelper'
import {
  SPECIAL_TYPE,
  TARGET_TYPE
} from '@/modules/dataMaintenance/constants/Special.constants'

const DEBOUNCE_GPP_TIMER = 1500

const SPECIAL_FIELDS = {
  selectedFamilyGroup: null,
  selectedProduct: null,
  endDate: null,
  startDate: null,
  targetType: null,
  targetBannerGroupIds: [],
  targetMultiStoreOperatorIds: [],
  targetStoreIds: [],
  regionalPriceMap: []
}

export default {
  components: {
    LegacyRadioGroup,
    iInstructions,
    iNumericField,
    iSearchPostCode,
    RetailerListActionButton,
    UpdatePriceToAllZones
  },

  props: {
    familyGroups: Array,
    pricingRegions: Array,
    products: Array,
    special: Object
  },

  data: () => ({
    calculatingProductPrice: false,
    calculatingProductPriceRegionCodes: [],
    debounceCalculatePrice: [],
    loadingStores: true,
    endDateMenu: false,
    startDateMenu: false,
    searchInput: '',
    productSearchInput: '',
    zoneFilter: '',
    originalSpecial: {},
    specialType: null,
    discountEntryMode: DISCOUNT_ENTRY_MODE.AMOUNT,
    saving: false,
    savedListApplied: false,
    targetBannerGroups: [],
    applyToAllZones: false,
    globalCartonPrice: null,
    globalUnitRrp: null,
    targetMultiStoreOperators: [],
    ...FieldValidators,
    ...SPECIAL_FIELDS,
    ...SPECIAL_TYPE,
    ...TARGET_TYPE,
    upcomingPriceChangeWarningText: UPCOMING_PRICE_CHANGE_WARNING_TEXT
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', [
      'isILR',
      'tenantCurrency',
      'tenantLocale',
      'tenantCountryRegion'
    ]),

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    discountEntryModeOptions() {
      return [
        { label: 'Amount', value: DISCOUNT_ENTRY_MODE.AMOUNT },
        { label: '% Discount', value: DISCOUNT_ENTRY_MODE.PERCENTAGE }
      ]
    },

    isPercentageDiscountMode() {
      return this.discountEntryMode === DISCOUNT_ENTRY_MODE.PERCENTAGE
    },

    amountOffLabel() {
      return this.isPercentageDiscountMode ? 'Discount %' : 'Amount Off'
    },

    amountOffPrefix() {
      return this.isPercentageDiscountMode ? '' : this.currencySymbol
    },

    amountOffSuffix() {
      return this.isPercentageDiscountMode ? '%' : undefined
    },

    amountOffMax() {
      return this.isPercentageDiscountMode ? 99.99 : 9999.99
    },

    newSpecial() {
      return _.isEmpty(this.special)
    },

    bulkZoneUpdatesDisabled() {
      return !this.newSpecial
    },

    activeSpecial() {
      return (
        this.special?.promotion?.status === 'ACTIVE' ||
        this.special?.promotion?.isEndDateAmendment
      )
    },
    hasRequestedAmendment() {
      return this.special?.promotion?.approval?.status === 'REJECTED'
    },
    canEditStartDate() {
      return (
        !!(this.selectedProduct || this.selectedFamilyGroup) &&
        (!this.activeSpecial ||
          (this.hasRequestedAmendment &&
            !this.special?.promotion?.isEndDateAmendment))
      )
    },
    canEditEndDate() {
      return !!(this.selectedProduct || this.selectedFamilyGroup)
    },
    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },
    isUsTenant() {
      return this.displayRules.isUSCountry
    },
    showIncTaxColumns() {
      return this.displayRules.showTaxSuffix
    },
    showUnitRrpColumns() {
      return !this.isServiceProductSelected && this.displayRules.showRrp
    },
    filteredRegionalPriceMap() {
      const term = (this.zoneFilter || '').trim().toLowerCase()
      if (!term) return this.regionalPriceMap
      return this.regionalPriceMap.filter((zone) => {
        const values = [zone.code, zone.name, zone.displayName, zone.state]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
        return values.some((v) => v.includes(term))
      })
    },

    beforeEndDate() {
      return this.startDate <= this.endDate
    },

    endDateDisplay() {
      return this.$options.filters.formatDatePicker(this.endDate)
    },

    changesMade() {
      return !_.isEqual(this.originalSpecial, this.createBluePrint())
    },

    familySpecial() {
      return this.specialType === this.BY_FAMILY_GROUP
    },

    pricingTableHeight() {
      return this.selectedFamilyGroup || this.selectedProduct ? '425px' : 'auto'
    },

    productSpecial() {
      return this.specialType === this.BY_PRODUCT
    },

    lockedProductLabel() {
      return `${this.special.product.supplierItemId} / ${this.special.product.tradeItemDescription}`
    },

    lockedFamilyGroupLabel() {
      return this.special.promotion.familyGroup.name
    },

    visibleProducts() {
      return buildVisibleProductOptions({
        options: this.products,
        searchTerm: this.productSearchInput,
        selectedValues: this.selectedProduct ? [this.selectedProduct] : []
      })
    },

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    showUnavailableProductAlert() {
      return (
        this.newSpecial &&
        (this.products.some((product) => product.disabled) ||
          this.familyGroups.some((familyGroup) => familyGroup.disabled))
      )
    },

    startDateDisplay() {
      return this.$options.filters.formatDatePicker(this.startDate)
    },

    minAllowedEndDate() {
      return minAllowedPromotionEndDate(
        this.startDate,
        this.tenantCountryRegion
      )
    },

    minAllowedStartDate() {
      return minAllowedPromotionStartDate(this.tenantCountryRegion)
    },

    variableWeightProductSelected() {
      return (
        !!this.selectedProduct &&
        !!this.selectedProduct.variableWeightTargetInKg
      )
    },

    isServiceProductSelected() {
      return this.selectedProduct?.productType === 'SERVICE'
    },

    // ***
    // Properties related to Special validation
    // ***

    hasNoPricedZones() {
      if (this.isServiceProductSelected) {
        return !this.regionalPriceMap.find(
          (zone) => zone.amountOff > 0 && zone.amountOff < zone.cartonPrice
        )
      }
      return SpecialsHelper.hasNoPricedZones(
        this.regionalPriceMap,
        this.isUsTenant
      )
    },

    hasIncompletePricing() {
      if (this.isServiceProductSelected || this.isUsTenant) return false
      return SpecialsHelper.hasIncompletePricing(this.regionalPriceMap)
    },

    hasInvalidPricing() {
      if (this.isServiceProductSelected || this.isUsTenant) {
        return !!this.regionalPriceMap.find(
          (zone) => zone.amountOff < 0 || zone.amountOff >= zone.cartonPrice
        )
      }
      return SpecialsHelper.hasInvalidPricing(
        this.regionalPriceMap,
        this.isUsTenant
      )
    },

    hasValidPricing() {
      if (this.isServiceProductSelected || this.isUsTenant) {
        return !!this.regionalPriceMap.find(
          (zone) => zone.amountOff > 0 && zone.amountOff < zone.cartonPrice
        )
      }
      return SpecialsHelper.hasValidPricing(
        this.regionalPriceMap,
        this.isUsTenant
      )
    },

    validSpecialDates() {
      return this.startDate && this.endDate >= this.startDate
    },

    validTargetType() {
      return (
        (this.targetType === this.BANNER_GROUP &&
          !!this.targetBannerGroupIds.length) ||
        (this.targetType === this.MULTI_STORE_OPERATOR &&
          !!this.targetMultiStoreOperatorIds.length) ||
        (this.targetType === this.RETAILER_STORE &&
          !!this.targetStoreIds.length) ||
        this.targetType === this.NATIONAL
      )
    },

    validPriceDrop() {
      return (
        this.startDate &&
        this.endDate &&
        this.validSpecialDates &&
        this.validTargetType &&
        this.validPromotionProductAvailability &&
        this.hasValidPricing &&
        !this.hasInvalidPricing &&
        !this.calculatingProductPrice
      )
    },

    validPromotionProductAvailability() {
      return (
        !productHasUpcomingPriceChange(
          this.selectedProduct,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        ) &&
        !familyGroupHasUpcomingPriceChange(
          this.selectedFamilyGroup,
          this.upcomingPriceChangeProductCodes
        )
      )
    }
  },

  watch: {
    discountEntryMode(newMode, oldMode) {
      if (oldMode && newMode !== oldMode) {
        this.clearDiscountPricing()
      }
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    amountOffInputValue(zone) {
      if (!this.isPercentageDiscountMode) return zone.amountOff
      if (zone.discountPercentage) return zone.discountPercentage
      return formatDiscountPercentage(
        discountPercentageFromAmountOff(zone.cartonPrice, zone.amountOff),
        this.tenantLocale
      )
    },

    onAmountOffInput(zone, value) {
      if (this.isPercentageDiscountMode) {
        zone.discountPercentage = value
        zone.amountOff = amountOffFromDiscountPercentage(
          zone.cartonPrice,
          value
        )
      } else {
        zone.amountOff = value
        setDiscountPercentageFromAmountOff(zone, zone.cartonPrice, value)
      }
      this.calcPricingForZone(zone)
      this.onZoneFieldChange(zone.code)
    },

    // ***
    // Functions related to help determining if Special has been modified
    // ***
    createBluePrint() {
      return {
        selectedFamilyGroup: this.selectedFamilyGroup,
        selectedProduct: this.selectedProduct,
        endDate: this.endDate,
        startDate: this.startDate,
        targetType: this.targetType,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        regionalPriceMap: this.regionalPriceMap
      }
    },

    // ***
    // Functions related to Special actions
    // ***

    copyPriceDrop() {
      this.selectedProduct = this.special.product

      this.selectedFamilyGroup = this.special.promotion.familyGroup

      this.selectedFamilyGroup
        ? (this.specialType = this.BY_FAMILY_GROUP)
        : (this.specialType = this.BY_PRODUCT)

      this.regionalPriceMap = this.special.regionalPriceMap

      this.startDate = this.special.promotion.startDate
      this.endDate = this.special.promotion.endDate

      this.targetBannerGroupIds =
        this.special.promotion.targetBannerGroups?.map(
          (bannerGroup) => bannerGroup.bannerGroupId
        ) || []
      this.targetMultiStoreOperatorIds =
        this.special.promotion.targetMultiStoreOperators?.map(
          (mso) => mso.multiStoreOperatorId
        ) || []
      this.targetStoreIds = this.special.promotion.targetStores?.map(
        (store) => store.storeId
      )
      this.targetType = this.determineTargetType()
    },

    savePriceDrop() {
      this.saving = true

      const payload = {
        familyGroupId: this.selectedFamilyGroup?.id,
        productCode: this.selectedProduct?.productCode,
        endDate: this.endDate,
        startDate: this.startDate,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        // Filter out zones without pricing
        zonePrices: this.regionalPriceMap
          .filter(
            (zone) =>
              zone.amountOff > 0 &&
              (this.isServiceProductSelected ||
                this.isUsTenant ||
                zone.unitRrp > 0)
          )
          .map((zone) => ({
            pricingRegionCode: zone.code,
            amountOff: zone.amountOff,
            unitRrp: zone.unitRrp || zone.revisedUnitPriceIncTax,
            revisedUnitPriceIncTax: zone.revisedUnitPriceIncTax
          }))
      }

      this.newSpecial
        ? this.createPriceDrop(payload)
        : this.updatePriceDrop(payload)
    },

    createPriceDrop(payload) {
      createPriceDrop(this.selectedSupplierId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Price Drop has been created!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          this.handleBackendError(err)
        })
    },

    updatePriceDrop(payload) {
      updatePriceDrop(
        this.selectedSupplierId,
        this.special.promotion.id,
        payload
      )
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Price Drop has been updated!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          this.handleBackendError(err)
        })
    },

    handleBackendError(err) {
      let msg
      switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
        case 'PROMOTION_OVERLAP':
          msg = 'There is an active Price Drop for this product on this date...'
          break
        case 'VOLUME_PROMOTION_OVERLAP':
          msg = 'There is an active Volume Buy for this product on this date...'
          break
        case 'UPCOMING_PRICE_RELATED_CHANGE_BEFORE_PROMOTION_END_DATE':
          msg =
            'Price related product edits exist before this Promotion ends. End Promotion before or delete upcoming product edits.'
          break
        default:
          msg = 'Price Drop could not be created / edited, please try again...'
      }
      this.addNotification({ color: 'error', text: msg, timeout: -1 })
    },

    // ***
    // Functions related regional pricemap creation
    // ***

    handleFamilyGroupSelection() {
      if (!this.selectedFamilyGroup) {
        this.regionalPriceMap = []
        return
      }

      if (
        familyGroupHasUpcomingPriceChange(
          this.selectedFamilyGroup,
          this.upcomingPriceChangeProductCodes
        )
      ) {
        this.selectedFamilyGroup = null
        this.regionalPriceMap = []
        return
      }

      // Finds family group products
      const familyProducts = this.products.filter((p) =>
        this.selectedFamilyGroup.productCodes.includes(p.value.productCode)
      )

      // Generate new regionalPriceMap from Family Products & pricing regions
      this.regionalPriceMap =
        SpecialsHelper.transformProductsToRegionalPriceMap(
          familyProducts.map((p) => p.value),
          this.pricingRegions,
          this.tenantCountryRegion
        ).sort((a, b) => (a.code > b.code ? 1 : -1))
    },

    handleProductSelection() {
      if (!this.selectedProduct) {
        this.regionalPriceMap = []
        return
      }

      if (
        productHasUpcomingPriceChange(
          this.selectedProduct,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        )
      ) {
        this.selectedProduct = null
        this.regionalPriceMap = []
        return
      }

      this.regionalPriceMap = SpecialsHelper.transformProductToRegionalPriceMap(
        this.selectedProduct,
        this.pricingRegions,
        this.tenantCountryRegion
      ).sort((a, b) => (a.code > b.code ? 1 : -1))
    },

    handleProductChange() {
      this.handleProductSelection()
      this.productSearchInput = ''
    },

    // ***
    // Functions related data mapping
    // ***

    mapBannerGroupsList() {
      // Map property names so that it works with V-select component
      this.targetBannerGroups = this.bannerGroups
        .map((bannerGroup) => ({
          title: bannerGroup.displayName,
          key: bannerGroup.id,
          text: bannerGroup.displayName,
          value: bannerGroup.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapMultiStoreOperatorsList() {
      // Map property names so that it works with V-select component
      this.targetMultiStoreOperators = this.multiStoreOperators
        .map((mso) => ({
          title: mso.displayName,
          key: mso.id,
          text: mso.displayName,
          value: mso.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetStores = this.retailerStores
          .map((store) => ({
            title: store.displayName,
            key: store.id,
            ...store,
            text: store.displayName,
            value: store.id
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))

        if (this.regionalPriceMap.length) {
          // Filter out stores not located in regions where the product is available
          this.targetStores = this.targetStores.filter((store) =>
            this.regionalPriceMap
              .map((p) => p.code)
              .includes(store.pricingRegionCode)
          )
        }
      } else {
        this.addNotification({
          color: 'secondary',
          text: `Available ${
            this.isILR ? 'Customers' : 'Retailers'
          } loading, please wait...`
        })
      }
    },

    isCalculatingProductPriceForRegion(pricingRegionCode) {
      return (
        this.calculatingProductPriceRegionCodes.indexOf(pricingRegionCode) !==
        -1
      )
    },

    // ***
    // Helper Functions
    // ***

    calcPricingForZone(zone) {
      if (zone.amountOff) {
        this.debounceCalcProductPriceForZone(zone)
      } else {
        zone.grossProfitPercentage = null
        zone.revisedUnitPriceExTax = null
        zone.revisedUnitPriceIncTax = null
        zone.revisedCostPerKGExTax = null
        zone.revisedCostPerKGIncTax = null
      }
    },

    debounceCalcProductPriceForZone(zone) {
      const regionCode = zone.code
      clearTimeout(this.debounceCalculatePrice[regionCode])
      this.debounceCalculatePrice[regionCode] = setTimeout(() => {
        this.calculatingProductPrice = true
        if (
          this.calculatingProductPriceRegionCodes.indexOf(regionCode) === -1
        ) {
          this.calculatingProductPriceRegionCodes.push(regionCode)
        }

        calculateProductPrice({
          cartonPriceExTax: zone.cartonPrice - zone.amountOff,
          packageQuantity: zone.packageQuantity,
          variableWeightTargetInKg: zone.variableWeightTargetInKg,
          // srpIncTax is the same as unitRrp
          srpIncTax: zone.unitRrp || null,
          hasGst: zone.hasGst,
          hasWet: zone.hasWet
        }).then((data) => {
          zone.grossProfitPercentage = data.gpp
          zone.revisedUnitPriceExTax = data.unitPriceExTax
          zone.revisedUnitPriceIncTax = data.unitPriceIncTax
          zone.revisedCostPerKGExTax = data.pricePerKgExTax
          zone.revisedCostPerKGIncTax = data.pricePerKgIncTax
          if (this.isServiceProductSelected) {
            zone.unitRrp = data.unitPriceIncTax
          }
          this.calculatingProductPrice = false
          this.removeCalculatingProductPriceRegionCode(regionCode)
        })
      }, DEBOUNCE_GPP_TIMER)
    },

    removeCalculatingProductPriceRegionCode(regionCode) {
      const regionCodeIndex =
        this.calculatingProductPriceRegionCodes.indexOf(regionCode)
      if (regionCodeIndex !== -1) {
        this.calculatingProductPriceRegionCodes.splice(regionCodeIndex, 1)
      }
    },

    determineTargetType() {
      if (
        Array.isArray(this.targetBannerGroupIds) &&
        this.targetBannerGroupIds.length
      )
        return this.BANNER_GROUP
      if (
        Array.isArray(this.targetMultiStoreOperatorIds) &&
        this.targetMultiStoreOperatorIds.length
      )
        return this.MULTI_STORE_OPERATOR
      if (Array.isArray(this.targetStoreIds) && this.targetStoreIds.length)
        return this.RETAILER_STORE
      return this.NATIONAL
    },

    getReferenceProductForOriginalRrp() {
      if (this.selectedProduct) return this.selectedProduct
      if (!this.selectedFamilyGroup?.productCodes?.length) return null

      const familyProducts = this.products
        .filter((p) =>
          this.selectedFamilyGroup.productCodes.includes(p.value.productCode)
        )
        .map((p) => p.value)

      if (!familyProducts.length) return null

      const lowestPackageQuantity = Math.min(
        ...familyProducts.map((product) => product.packageQuantity)
      )
      return (
        familyProducts.find(
          (product) => product.packageQuantity === lowestPackageQuantity
        ) || null
      )
    },

    getOriginalProductRrp(zone) {
      const zoneOriginalRrp = Number(zone?.originalUnitRrp)
      if (!isNaN(zoneOriginalRrp) && zoneOriginalRrp > 0) return zoneOriginalRrp

      const referenceProduct = this.getReferenceProductForOriginalRrp()
      const fallbackRrp = Number(
        referenceProduct?.regionalPriceMap?.[zone.code]?.[0]?.rrp
      )

      return !isNaN(fallbackRrp) && fallbackRrp > 0 ? fallbackRrp : null
    },

    onZoneFieldChange(zoneCode) {
      /*
        For each zone, a form wraps the v-text-field element.
        On field change, we trigger form validation manually as
        :rules validator is not reactive unless field clicked first
        in our case we need reactivity even if field not clicked yet
      */
      this.$nextTick(() => {
        const amountForm = this.$refs[`formAmountOff${zoneCode}`]
        const rrpForm = this.$refs[`formSpecialUnitRrp${zoneCode}`]

        if (amountForm && Array.isArray(amountForm)) amountForm[0].validate()
        if (rrpForm && Array.isArray(rrpForm)) rrpForm[0].validate()
      })
    },

    revisedUnitPrice(zone) {
      return _.round(
        (zone.cartonPrice - zone.amountOff) / zone.packageQuantity,
        2
      )
    },

    revisedCostPerKG(zone) {
      return _.round(
        (zone.cartonPrice - zone.amountOff) / zone.variableWeightTargetInKg,
        2
      )
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []
    },

    resetSpecialForm() {
      this.selectedProduct = null
      this.selectedFamilyGroup = null
      this.targetType = null
      this.resetTargetTypeForm()
    },

    handleApplySavedRetailers(ids) {
      this.targetStoreIds = [...ids]
      this.savedListApplied = true
    },

    resetRetailers() {
      this.targetStoreIds = []
      this.savedListApplied = false
    },

    onGlobalCartonUpdate(value) {
      this.regionalPriceMap.forEach((zone) => {
        zone.amountOff = value
        setDiscountPercentageFromAmountOff(zone, zone.cartonPrice, value)
        this.calcPricingForZone(zone)
        this.onZoneFieldChange(zone.code)
      })
    },

    onGlobalDiscountPercentageUpdate(value) {
      this.regionalPriceMap.forEach((zone) => {
        zone.discountPercentage = value
        zone.amountOff = amountOffFromDiscountPercentage(
          zone.cartonPrice,
          value
        )
        this.calcPricingForZone(zone)
        this.onZoneFieldChange(zone.code)
      })
    },

    onGlobalUnitRrpUpdate(value) {
      if (this.isServiceProductSelected) return

      this.regionalPriceMap.forEach((zone) => {
        zone.unitRrp = value
        this.handleZonePricingInput(zone)
      })
    },

    clearDiscountPricing() {
      this.regionalPriceMap.forEach((zone) => {
        zone.amountOff = null
        delete zone.discountPercentage
        zone.grossProfitPercentage = null
        zone.revisedUnitPriceExTax = null
        zone.revisedUnitPriceIncTax = null
        this.onZoneFieldChange(zone.code)
      })
    },

    handleZonePricingInput(zone) {
      this.calcPricingForZone(zone)
      this.onZoneFieldChange(zone.code)
    },

    getFamilyGroupProductsById(familyGroupId) {
      if (!familyGroupId) return []

      const group = this.familyGroups.find((f) => f.value?.id === familyGroupId)
      if (!group || !group.value?.productCodes?.length) return []

      return group.value.productCodes
        .map((code) => {
          const product = this.products.find(
            (p) => p.value?.productCode === code
          )
          return product ? product.value.tradeItemDescription : null
        })
        .filter(Boolean)
    }
  },

  mounted() {
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
    this.mapStoresList()
    if (!this.newSpecial) {
      this.copyPriceDrop()
    } else {
      this.targetType = this.NATIONAL
    }
    // Create blue print of special so that we can know if changes were made
    this.originalSpecial = _.cloneDeep(this.createBluePrint())

    if (this.newSpecial) {
      this.targetType = this.NATIONAL
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#edit-price-drops {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;

  .amount-off-helper {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 16px;
    margin-top: 2px;
    text-align: right;
  }

  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}

.v-tooltip__content {
  background-color: #2e2e2e !important;
  color: #fff !important;
}

@media only screen and (min-width: 960px) {
  #edit-price-drops {
    th span {
      font-size: 14px;
    }
  }
}
</style>
<style lang="scss">
#edit-price-drops {
  .data-table-zone-pricing {
    td {
      &.text-only {
        padding-top: 16px !important;
      }
      padding: 10px !important;
      vertical-align: top;
    }

    .v-text-field__details {
      padding: 0 4px !important;
    }

    .v-messages {
      .v-messages__message {
        font-size: 0.9rem;
      }
    }
  }
}
</style>
